import { useCallback, useState } from 'react';
import { RcFile } from 'antd/lib/upload';
import {
  showErrorNotification,
  showSuccessNotification,
} from 'services/common';
import useMap from 'react-use/lib/useMap';
import {
  createUploadUrls,
  createEmailExclusionJobFromUrls,
} from 'services/acg/phase-1';
import { uploadFileUsingGCSUrl } from 'services/utils';
import _ from 'lodash';

type CreateEmailExclusionJobData = {
  exclusionFiles: RcFile[],
  campaignFiles: {
    folder: string,
    files: RcFile[],
    postExclusionCriteria: {
      domains: string[],
      providers: string[],
      ranks: number[],
      companyNameRegex: string,
    },
  }[],
};

const validateData = (data: CreateEmailExclusionJobData) => {
  if (!data.exclusionFiles.length) {
    throw new Error('No exclusion files provided');
  }

  if (!data.campaignFiles.length) {
    throw new Error('No campaign files provided');
  }

  // Validate unique exclusion files
  const exclusionFileNames = data.exclusionFiles.map(file => file.name);
  const uniqueExclusionFileNames = [...new Set(exclusionFileNames)];
  if (exclusionFileNames.length !== uniqueExclusionFileNames.length) {
    throw new Error('Duplicate exclusion files provided');
  }

  // Validate unique campaign folders
  const campaignFolders = data.campaignFiles.map(folder => folder.folder);
  const uniqueCampaignFolders = [...new Set(campaignFolders)];
  if (campaignFolders.length !== uniqueCampaignFolders.length) {
    throw new Error('Duplicate campaign folders provided');
  }

  // Validate unique campaign files in each folder
  data.campaignFiles.forEach(folder => {
    const campaignFileNames = folder.files.map(file => file.name);
    const uniqueCampaignFileNames = [...new Set(campaignFileNames)];
    if (campaignFileNames.length !== uniqueCampaignFileNames.length) {
      throw new Error(
        `Duplicate campaign files provided in folder ${folder.folder}`,
      );
    }
    if (!folder.files.length) {
      throw new Error(`No campaign files provided in folder ${folder.folder}`);
    }
  });
};

const createCreateUploadUrlsPayload = (data: CreateEmailExclusionJobData) => {
  const campaignFiles = {};
  data.campaignFiles.forEach(folder => {
    campaignFiles[folder.folder] = folder.files.map(file => {
      return {
        filename: file.name,
      };
    });
  });
  return {
    exclusionFiles: data.exclusionFiles.map(file => ({
      filename: file.name,
    })),
    campaignFiles,
  };
};

// eslint-disable-next-line import/prefer-default-export
export const useCreateEmailExclusionJob = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, { set, reset }] = useMap({});
  const doUpload = useCallback(async (data: CreateEmailExclusionJobData) => {
    setIsLoading(true);
    try {
      validateData(data);
      const uploadUrls = await createUploadUrls(
        createCreateUploadUrlsPayload(data),
      );
      const payloadToCreateJob = {
        exclusionFiles: [],
        campaignFiles: {},
      };
      const uploadPromises = [];
      data.exclusionFiles.forEach((file, index) => {
        const { uploadUrl, gcsUrl } = uploadUrls.exclusionFiles[index];
        uploadPromises.push(
          uploadFileUsingGCSUrl(uploadUrl, file, progressEvent => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            set(`exclusionFiles:${file.name}`, percentCompleted);
          }).then(() => {
            payloadToCreateJob.exclusionFiles.push({
              filename: file.name,
              gcsUrl,
            });
          }),
        );
      });
      data.campaignFiles.forEach((folder, folderIndex) => {
        payloadToCreateJob.campaignFiles[folder.folder] = {
          priority: folderIndex,
          files: [],
          postExclusionCriteria: folder.postExclusionCriteria,
        };
        folder.files.forEach((file, index) => {
          const { uploadUrl, gcsUrl } = uploadUrls.campaignFiles[folder.folder][
            index
          ];
          uploadPromises.push(
            uploadFileUsingGCSUrl(uploadUrl, file, progressEvent => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              set(
                `campaignFiles:${folder.folder}:${file.name}`,
                percentCompleted,
              );
            }).then(() => {
              payloadToCreateJob.campaignFiles[folder.folder].files.push({
                filename: file.name,
                gcsUrl,
              });
            }),
          );
        });
      });
      const chunkedUploadPromises = _.chunk(uploadPromises, 7);
      for await (const chunk of chunkedUploadPromises) {
        await Promise.all(chunk);
      }
      await createEmailExclusionJobFromUrls(payloadToCreateJob);
      showSuccessNotification('Email exclusion job created successfully');
    } catch (error) {
      showErrorNotification(error.message);
      throw error;
    } finally {
      reset();
      setIsLoading(false);
    }
  }, []);
  return {
    mutate: doUpload,
    isLoading,
    progress,
  };
};
